import * as React from 'react';
import {
  Alert, Box, Button, CircularProgress, FormControl, InputLabel,
  MenuItem, Paper, Select, TextField, Typography, RadioGroup,
  FormControlLabel, Radio,
} from '@mui/material';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import api from '../services/api';
import moment from 'moment';

const SEVERITY_LABELS = {
  1: 'Info', 2: 'Aviso', 3: 'Media', 4: 'Alta', 5: 'Desastre', 6: 'API Down', 7: 'Backup',
};

export default function TabChamado({ props, state, setState, showNotify, athenaConfigs }) {
  const [ticketConfigId, setTicketConfigId] = React.useState('');
  const [ticketSubject, setTicketSubject] = React.useState('');
  const [ticketDescription, setTicketDescription] = React.useState('');
  const [ticketLoading, setTicketLoading] = React.useState(false);
  const [ticketResult, setTicketResult] = React.useState(null);

  const isBulk = Array.isArray(props.eventId);

  // Bulk mode
  const [bulkTicketMode, setBulkTicketMode] = React.useState('single');
  const [bulkTicketResults, setBulkTicketResults] = React.useState(null);

  React.useEffect(() => {
    if (athenaConfigs.length === 1) setTicketConfigId(athenaConfigs[0].id);
    setTicketResult(null);
    setBulkTicketResults(null);
    buildDefaults();
  }, [athenaConfigs, props.eventId]);

  const buildDefaults = () => {
    if (isBulk) {
      const n = props.eventId.length;
      setTicketSubject(`Reconhecimento em massa - ${n} alarmes`);
      const alarms = props.bulkAlarms || [];
      const lines = alarms.length > 0
        ? alarms.map(a => `- ${a.hostname || '?'} (${a.ip || '?'}): ${a.name || '?'}`).join('\n')
        : (Array.isArray(props.detailAlarms) ? props.detailAlarms : []).map(d => `- ${d}`).join('\n');
      setTicketDescription(`Reconhecimento em massa de ${n} alarmes:\n${lines}`);
    } else {
      const sevLabel = SEVERITY_LABELS[props.severity] || props.severity;
      setTicketSubject(`[Horus] ${props.detailAlarms || ''} - ${props.hostname || ''}`);
      setTicketDescription(
        `Host: ${props.hostname || '—'} (${props.ip || '—'})\n` +
        `Severidade: ${sevLabel}\n` +
        `Horario: ${props.clock ? moment(props.clock).format('DD/MM/YYYY HH:mm:ss') : '—'}\n` +
        `Ocorrencias: ${props.occurrenceCount || 1}\n` +
        `Alarme: ${props.detailAlarms || '—'}\n` +
        `Source: ${props.source || 'zabbix'}`
      );
    }
  };

  const resolveMacros = (template, alarm) => {
    return template
      .replace(/\{host\}/g, alarm.hostname || '—')
      .replace(/\{ip\}/g, alarm.ip || '—')
      .replace(/\{empresa\}/g, alarm.cod_empresa || '')
      .replace(/\{alarme\}/g, alarm.name || '—')
      .replace(/\{severidade\}/g, SEVERITY_LABELS[alarm.severity] || alarm.severity || '—')
      .replace(/\{horario\}/g, alarm.clock ? moment(alarm.clock).format('DD/MM/YYYY HH:mm:ss') : '—')
      .replace(/\{ocorrencias\}/g, alarm.occurrences || 1);
  };

  const appendTicketToDescription = (ticketId) => {
    if (ticketId && setState) {
      setState(prev => ({
        ...prev,
        description: prev.description
          ? prev.description + `\nSendo tratado no ticket #${ticketId}`
          : `Sendo tratado no ticket #${ticketId}`,
      }));
    }
  };

  const handleSendTicket = () => {
    if (!ticketConfigId) return;
    setTicketLoading(true);
    setTicketResult(null);
    api.post('dashboard/ticket/athena/', {
      config_id: ticketConfigId,
      subject: ticketSubject,
      description: ticketDescription,
    })
      .then(res => {
        const body = res.data?.response_body;
        let parsed = null;
        try { parsed = typeof body === 'string' ? JSON.parse(body) : body; } catch {}
        const ticketId = parsed?.ticket_id || parsed?.id;
        setTicketResult({ success: true, ticketId });
        showNotify(ticketId ? `Chamado #${ticketId} criado!` : 'Chamado criado!', 'success');
        appendTicketToDescription(ticketId);
      })
      .catch(err => {
        const detail = err.response?.data?.detail || 'Erro ao criar chamado';
        setTicketResult({ success: false, error: detail });
        showNotify(detail, 'error');
      })
      .finally(() => setTicketLoading(false));
  };

  const handleSendBulkTicket = async () => {
    if (!ticketConfigId) return;
    setTicketLoading(true);
    setBulkTicketResults(null);

    if (bulkTicketMode === 'single') {
      try {
        const res = await api.post('dashboard/ticket/athena/', {
          config_id: ticketConfigId,
          subject: ticketSubject,
          description: ticketDescription,
        });
        const body = res.data?.response_body;
        let parsed = null;
        try { parsed = typeof body === 'string' ? JSON.parse(body) : body; } catch {}
        const ticketId = parsed?.ticket_id || parsed?.id;
        setBulkTicketResults({ success: true, total: 1, created: 1, ticketId });
        showNotify(ticketId ? `Chamado #${ticketId} criado!` : 'Chamado criado!', 'success');
        appendTicketToDescription(ticketId);
      } catch (err) {
        const detail = err.response?.data?.detail || 'Erro ao criar chamado';
        setBulkTicketResults({ success: false, error: detail });
        showNotify(detail, 'error');
      }
    } else {
      const alarms = props.bulkAlarms || [];
      if (alarms.length === 0) {
        showNotify('Dados dos alarmes nao disponiveis para envio individual', 'error');
        setTicketLoading(false);
        return;
      }
      let created = 0, errors = 0;
      const ticketIds = [];
      for (const alarm of alarms) {
        const subject = resolveMacros(ticketSubject, alarm);
        const description = resolveMacros(ticketDescription, alarm);
        try {
          const res = await api.post('dashboard/ticket/athena/', { config_id: ticketConfigId, subject, description });
          const body = res.data?.response_body;
          let parsed = null;
          try { parsed = typeof body === 'string' ? JSON.parse(body) : body; } catch {}
          const tid = parsed?.ticket_id || parsed?.id;
          if (tid) ticketIds.push(tid);
          created++;
        } catch { errors++; }
      }
      setBulkTicketResults({ success: errors === 0, total: alarms.length, created, errors });
      if (errors === 0) showNotify(`${created} chamado(s) criado(s)!`, 'success');
      else showNotify(`${created} criados, ${errors} com erro`, errors === alarms.length ? 'error' : 'warning');
      if (ticketIds.length > 0 && setState) {
        const txt = ticketIds.map(id => `#${id}`).join(', ');
        setState(prev => ({
          ...prev,
          description: prev.description
            ? prev.description + `\nSendo tratado nos tickets ${txt}`
            : `Sendo tratado nos tickets ${txt}`,
        }));
      }
    }
    setTicketLoading(false);
  };

  if (athenaConfigs.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Nenhuma configuracao Athena encontrada para esta empresa.
        </Typography>
      </Box>
    );
  }

  const results = isBulk ? bulkTicketResults : ticketResult;

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          <ConfirmationNumberIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
          Abrir Chamado - Athena
        </Typography>

        {athenaConfigs.length > 1 && (
          <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
            <InputLabel>Webhook</InputLabel>
            <Select
              value={ticketConfigId} label="Webhook"
              onChange={(e) => setTicketConfigId(e.target.value)}
            >
              {athenaConfigs.map(c => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}{c.cod_empresa === 0 ? ' (default)' : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {isBulk && (
          <FormControl sx={{ mb: 1.5 }}>
            <RadioGroup row value={bulkTicketMode} onChange={(e) => {
              setBulkTicketMode(e.target.value);
              const n = props.eventId.length;
              if (e.target.value === 'single') {
                buildDefaults();
              } else {
                setTicketSubject('[Horus] {alarme} - {host}');
                setTicketDescription(
                  'Host: {host} ({ip})\nSeveridade: {severidade}\nHorario: {horario}\nOcorrencias: {ocorrencias}\nAlarme: {alarme}'
                );
              }
            }}>
              <FormControlLabel value="single" control={<Radio size="small" />} label="Chamado unico" />
              <FormControlLabel value="per_alarm" control={<Radio size="small" />} label="Um por alarme" />
            </RadioGroup>
          </FormControl>
        )}

        <TextField
          fullWidth size="small" label="Assunto" sx={{ mb: 1.5 }}
          value={ticketSubject} onChange={(e) => setTicketSubject(e.target.value)}
        />
        <TextField
          fullWidth size="small" label="Descricao" multiline rows={6}
          value={ticketDescription} onChange={(e) => setTicketDescription(e.target.value)}
        />

        {isBulk && bulkTicketMode === 'per_alarm' && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            Macros: {'{host}'} {'{ip}'} {'{empresa}'} {'{alarme}'} {'{severidade}'} {'{horario}'} {'{ocorrencias}'}
          </Typography>
        )}

        {results && (
          <Alert severity={results.success ? 'success' : 'error'} sx={{ mt: 1.5 }}>
            {results.success
              ? results.ticketId
                ? `Chamado #${results.ticketId} criado!`
                : results.total > 1
                  ? `${results.created} chamado(s) criado(s)!`
                  : 'Chamado criado!'
              : results.error || `${results.created} criados, ${results.errors} com erro`}
          </Alert>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button
            variant="contained"
            onClick={isBulk ? handleSendBulkTicket : handleSendTicket}
            disabled={ticketLoading || !ticketConfigId || results?.success}
            startIcon={ticketLoading ? <CircularProgress size={18} /> : <ConfirmationNumberIcon />}
          >
            {ticketLoading ? 'Enviando...' : 'Enviar Chamado'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
